/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type {
  InputConfig,
  ValueConfig,
  Tooltip,
} from "@smartsuite/automation-engine-api";
import { t } from "i18next";
import type { MutableRefObject } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRootStore } from "../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../../types/candidate";
import { StyledActionInputsContainer } from "../../../Sections/ActionSection/style";
import { FieldEditorRow } from "./FieldEditorRow";
import {
  StyledAddButtonWrapper,
  StyledGroupTitle,
  StyledGroupTitleWrapper,
  StyledGroupWrapper,
  StyledTooltipWrapper,
} from "./style";
import { translateIfPresent } from "../../../../../locales/i18n";
import { isTruthy } from "remeda";
import { isValueExist } from "../../../../../services/isValueExist";
import { observer } from "mobx-react-lite";
import {
  isRecordAction,
  isInputInFieldsGroup,
  hasFieldValueOrClearOperator,
} from "../../../../../utils/action-helpers";
import { getLocalCssColorVariables } from "../../../../../utils/cssVariablesUtils";
import * as A from "../../../../../stores/automation/mergers/actions";
import { InputFieldOptions } from "./InputFieldOptions";
import { InputNestedFieldOptions } from "./InputNestedFieldOptions";
import { shouldShowForNestedFields } from "../utils";
import { useUpstreamActions } from "../hooks/use-upstream-actions";
import {
  Icon,
  Title,
  TooltipAdvancedContent,
  TooltipButton,
  Tooltip as TooltipComponent,
  Text,
  AddButton,
  Paragraph,
  DropdownPanel,
} from "@smartsuite/react-ui/lib";
import {
  GrayColors,
  SolutionColorVariables,
  TextColorNames,
} from "@smartsuite/types";

interface FieldEditorProps {
  fieldInputs: InputCandidate[];
  loading: boolean;
  action: ActionCandidate;
  onFieldInputChange: (input: InputCandidate, action: ActionCandidate) => void;
  onRemoveFieldInput: (input: InputCandidate) => void;
  showValidationError: boolean;
  groupLabel: string;
  groupSubLabel: string;
  foldedByDefault: boolean;
  collapsible: boolean;
  tooltip?: Tooltip;
  addButtonLabel?: string;
  hide?: boolean;
}

export const FieldEditor = observer<FieldEditorProps>(
  ({
    fieldInputs,
    loading,
    action,
    onFieldInputChange,
    showValidationError,
    groupLabel,
    groupSubLabel,
    foldedByDefault,
    collapsible,
    tooltip,
    addButtonLabel,
    onRemoveFieldInput,
    hide,
  }) => {
    const store = useRootStore();
    const menuButtonRef = useRef(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const { candidate } = store.automations;
    // Only actions upstream of this one on its own path (no sibling-branch
    // outputs) are offered as data sources -- spec 8.1/8.2.
    const scopedActions = useUpstreamActions(candidate, action);
    const [options, setOptions] = useState<InputConfig[]>([]);
    const [fieldToShow, setFieldToShow] = useState<InputCandidate[]>([]);
    const [collapsed, setCollapsed] = useState(false);
    const [tab, setTab] = useState<"root" | "sub-menu">("root");
    const [subMenuOptions, setSubMenuOptions] = useState<InputConfig[]>([]);
    const [subMenuTitle, setSubMenuTitle] = useState<string>("[]");
    const actionGroupType =
      candidate.currentActionGroup?.config.oneofKind ?? "actions";

    const getIsFoldedByDefault = useCallback(
      (inputId: string): boolean => {
        const display = action.description.inputs.find(
          (i) => inputId === i.inputId,
        )?.display?.display;
        if (display?.oneofKind === "group") {
          return display.group.foldedByDefault;
        }
        return false;
      },
      [action.description.inputs],
    );

    const isFieldShouldBeDisplayed = useCallback(
      (input: InputCandidate): boolean =>
        isTruthy(input.isOpen) ||
        isValueExist(input) ||
        input.operatorConfig?.operator.oneofKind !== undefined ||
        !getIsFoldedByDefault(input.inputId),
      [getIsFoldedByDefault],
    );

    const onClickFieldButton = (): void => {
      if (options.length > 0) {
        setMenuOpen(!menuOpen);
      }
    };

    const onClickMenu = (inputDesc: InputConfig): void => {
      const selectedInput = fieldInputs.find(
        (fieldInput) => fieldInput.inputId === inputDesc.inputId,
      );
      setMenuOpen(false);

      if (selectedInput !== undefined) {
        const openedInput: InputCandidate = {
          ...inputDesc,
          isOpen: foldedByDefault,
        };
        onFieldInputChange(openedInput, action);
        setOptions([...options, inputDesc]);
        store.inputToFocus = openedInput;
      }
      setTab("root");
    };

    const onClickTrash = (inputConfig: InputCandidate): void => {
      const emptyValue: ValueConfig = {
        values: [],
      };

      const emptyInput: InputCandidate = {
        ...inputConfig,
        value: emptyValue,
        operatorConfig: { operator: { oneofKind: undefined } },
        isOpen: !foldedByDefault,
      };
      onFieldInputChange(emptyInput, action);
      setOptions([...options, inputConfig]);
    };

    useEffect(() => {
      if (isTruthy(showValidationError)) {
        const requiredFieldsIds = action.description.inputs
          .filter((input) => input.editor!.required)
          .map((input) => input.inputId);
        const newInputs = fieldInputs.map((input) => {
          if (requiredFieldsIds.includes(input.inputId)) {
            return { ...input, isOpen: foldedByDefault };
          }
          return { ...input };
        });

        candidate.setActions(
          [A.mergeInputs(action.hash, newInputs)],
          actionGroupType,
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showValidationError]);

    useEffect(() => {
      setFieldToShow(
        fieldInputs.filter((input) => isFieldShouldBeDisplayed(input)),
      );
      const availableOptions = fieldInputs.filter(
        (input) => !isFieldShouldBeDisplayed(input),
      );

      setOptions(
        availableOptions.filter((input) =>
          shouldShowForNestedFields(input, fieldInputs),
        ),
      );
    }, [fieldInputs, isFieldShouldBeDisplayed, showValidationError]);

    const goSubMenu = (options: InputConfig[], label: string): void => {
      setSubMenuTitle(label);
      setSubMenuOptions(options);
      setTab("sub-menu");
    };

    const onNavigateBack = useCallback(() => {
      setSubMenuOptions([]);
      setTab("root");
    }, []);

    const shouldShowFieldsEmptyError = useMemo(() => {
      if (!showValidationError) {
        return false;
      }

      const actionId = action.actionReference?.actionId;
      if (!isRecordAction(actionId)) {
        return false;
      }

      if (fieldInputs.length === 0) {
        return false;
      }

      const firstInput = fieldInputs[0];
      const inputDescription = action.description.inputs.find(
        (desc) => desc.inputId === firstInput.inputId,
      );

      if (!isInputInFieldsGroup(inputDescription)) {
        return false;
      }

      const hasFieldsWithValues = fieldInputs.some(
        hasFieldValueOrClearOperator,
      );

      return !hasFieldsWithValues;
    }, [
      showValidationError,
      action.actionReference?.actionId,
      action.description.inputs,
      fieldInputs,
    ]);

    return (
      <StyledGroupWrapper style={hide ? { visibility: "hidden" } : {}}>
        <StyledGroupTitle>
          {groupLabel !== "" && (
            <StyledGroupTitleWrapper
              className={collapsible ? "collapsible" : ""}
              onClick={() => {
                setCollapsed(collapsible ? !collapsed : false);
              }}
            >
              {collapsible && (
                <Icon
                  name={"arrow-down-small"}
                  color={TextColorNames.primary}
                  size="XS"
                  className={collapsed ? "collapsed" : ""}
                />
              )}
              <Title size="s" weight="medium">
                {translateIfPresent(
                  `field-editor.${groupLabel.toLowerCase()}`,
                  groupLabel,
                )}
                {tooltip !== undefined && (
                  <StyledTooltipWrapper>
                    <TooltipComponent
                      display="inline-flex"
                      maxWidth={360}
                      placements={["right"]}
                      padding={2}
                      text={tooltip.text}
                      target={
                        <TooltipButton
                          iconSize="S"
                          iconName="info-filled"
                          colors={{
                            hover: GrayColors["G-7"],
                          }}
                        />
                      }
                      renderTooltipContent={
                        <TooltipAdvancedContent
                          minWidth={340}
                          imageWidth={340}
                          imageHeight={220}
                          paddingType="uniform"
                          imageUrl={tooltip.iconUrl}
                          text="Specify the message that should be displayed in Notification Center"
                          textSize="xs"
                          textWeight="semibold"
                        />
                      }
                    />
                  </StyledTooltipWrapper>
                )}
              </Title>
            </StyledGroupTitleWrapper>
          )}
          {groupSubLabel !== "" && (
            <Text size="m" color="secondary">
              {translateIfPresent(`caption.${groupSubLabel}`, groupSubLabel)}
            </Text>
          )}
        </StyledGroupTitle>
        {!collapsed && (
          <>
            <StyledActionInputsContainer theme={{ loading }}>
              {fieldToShow.map((input) => {
                return (
                  <FieldEditorRow
                    key={`${action.hash}-${input.inputId}`}
                    input={input}
                    onChange={(input) => {
                      onFieldInputChange(input, action);
                    }}
                    trigger={candidate.self.trigger}
                    actions={scopedActions}
                    action={action}
                    integrations={store.integrations.list}
                    showValidationError={showValidationError}
                    onClickTrash={onClickTrash}
                    onRemoveFieldInput={onRemoveFieldInput}
                  />
                );
              })}
            </StyledActionInputsContainer>
            {options.length > 0 && (
              <StyledAddButtonWrapper
                onClick={onClickFieldButton}
                ref={menuButtonRef}
              >
                <AddButton
                  iconStyle={{
                    color: SolutionColorVariables.sc1,
                    hoverColor: SolutionColorVariables.sc3,
                  }}
                  textStyle={{
                    size: "xs",
                    weight: "regular",
                    color: SolutionColorVariables.sc1,
                    hoverColor: SolutionColorVariables.sc3,
                  }}
                >
                  <Paragraph size="s" weight="medium" color="link">
                    {addButtonLabel === undefined
                      ? t("field-editor.add-field")
                      : translateIfPresent(
                          `field-editor.${addButtonLabel
                            .replace(/\s+/, "-")
                            .toLowerCase()}`,
                          addButtonLabel,
                        )}
                  </Paragraph>
                </AddButton>
              </StyledAddButtonWrapper>
            )}
            {shouldShowFieldsEmptyError && (
              <Paragraph
                color="error"
                size="xs"
                style={{ marginTop: options.length > 0 ? "8px" : "0px" }}
              >
                {t("automation-form.at-least-one-field-required")}
              </Paragraph>
            )}
            <DropdownPanel
              enableWidthCalculation={true}
              isActive={menuOpen}
              targetElementRef={
                menuButtonRef as unknown as MutableRefObject<HTMLElement>
              }
              portalStyles={getLocalCssColorVariables(store.theme)}
              onClickOutside={() => {
                setMenuOpen(false);
              }}
              at="left bottom+5"
            >
              {tab === "root" && (
                <InputFieldOptions
                  goSubMenu={goSubMenu}
                  options={options}
                  action={action}
                  onClickMenu={onClickMenu}
                />
              )}
              {tab === "sub-menu" && (
                <InputNestedFieldOptions
                  onNavigateBack={onNavigateBack}
                  options={subMenuOptions}
                  action={action}
                  onClickMenu={onClickMenu}
                  subMenuTitle={subMenuTitle}
                />
              )}
            </DropdownPanel>
          </>
        )}
      </StyledGroupWrapper>
    );
  },
);
